<template>
  <div class="wrapper">
    <jade-navigation-bar
      @onNavigate="redirectToCompany"
      background="body"
      navigateType="close"
      :label="$t('navigate')"
    />
    <div class="container container--kyc-steps">
      <jade-stepper
        v-if="isStepperVisible"
        class="stepper"
        :steps="steps"
      />
      <jade-alert
        v-if="isAlertVisible"
        class="alert"
        variant="negative"
        showIcon
      >
        {{ $t('alert-qrcode') }}
      </jade-alert>
      <contact-owner
        v-if="isKycInContactOwnerStatus"
        :merchantId="merchantId"
      />
      <div
        v-else
        class="container--kyc-content"
      >
        <denied v-if="isKycInDeniedStatus" />
        <concluded v-if="isKycInConcludedStatus" />
        <validation
          v-if="isKycInValidtaionStatus"
          :date="updatedAtDateTime.date"
          :time="updatedAtDateTime.time"
        />
        <partially-denied
          v-if="isKycInPartiallyDeniedStatus"
          @createKycCodeError="onError"
          @updateKycStatus="updateKycStatus"
        />
        <personalization
          v-if="isKycInPersonalizationStatus"
          :merchantId="merchantId"
          :accountId="accountId"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import JadeNavigationBar from '@/components/jade/JadeNavigationBar'
import JadeStepper from '@/components/jade/JadeStepper'
import JadeAlert from '@/components/jade/JadeAlert'

import { EXTERNAL_ENDPOINTS } from '@/external-endpoints/index.js'

import Concluded from './steps/Concluded.vue'
import Validation from './steps/Validation.vue'
import PartiallyDenied from './steps/PartiallyDenied'
import Personalization from './steps/Personalization.vue'
import ContactOwner from './steps/ContactOwner.vue'
import Denied from './steps/Denied.vue'

import KycManager from './kyc.manager'

export default {
  name: 'KycContainer',
  components: {
    Denied,
    Concluded,
    Validation,
    PartiallyDenied,
    Personalization,
    ContactOwner,
    JadeNavigationBar,
    JadeStepper,
    JadeAlert
  },
  data () {
    return {
      showAlert: false
    }
  },
  computed: {
    ...mapGetters([
      'qrCode',
      'kyc',
      'loadingKycQrCode',
      'isKycOnboardingPending',
      'profile'
    ]),
    kycManager () {
      return new KycManager({
        kycList: this.kyc,
        onboarding: this.isKycOnboardingPending,
        merchantType: this.profile.merchant.type.key
      })
    },
    isAlertVisible () {
      return this.showAlert && !this.loadingKycQrCode
    },
    merchantId () {
      return this.$route.params.merchant_id
    },
    accountId () {
      return this.$route.params.account_id
    },
    urlQrCode () {
      return this.qrCode?.url
    },
    base64QrCode () {
      return this.qrCode?.base64_qrcode
    },
    isStepperVisible () {
      return this.kycManager.isStepperVisible()
    },
    isStepperValidationActive () {
      return this.kycManager.isStepperValidationActive()
    },
    isStepperPersonalizationActive () {
      return this.kycManager.isStepperPersonalizationActive()
    },
    isStepperConcludedActive () {
      return this.kycManager.isStepperConcludedActive()
    },
    isKycInContactOwnerStatus () {
      return this.kycManager.isKycInContactOwnerStatus()
    },
    isKycInValidtaionStatus () {
      return this.kycManager.isKycInValidtaionStatus()
    },
    isKycInPartiallyDeniedStatus () {
      return this.kycManager.isKycInPartiallyDeniedStatus()
    },
    isKycInPersonalizationStatus () {
      return this.kycManager.isKycInPersonalizationStatus()
    },
    isKycInConcludedStatus () {
      return this.kycManager.isKycInConcludedStatus()
    },
    isKycInDeniedStatus () {
      return this.kycManager.isKycInDeniedStatus()
    },
    updatedAtDateTime () {
      return this.kycManager.updatedAtDateTime()
    },
    steps () {
      return [
        {
          icon: 'person-check',
          actived: this.isStepperValidationActive,
          name: this.$t('validation')
        },
        {
          icon: 'shop',
          actived: this.isStepperPersonalizationActive,
          name: this.$t('personalization')
        },
        {
          icon: 'circle-check',
          actived: this.isStepperConcludedActive,
          name: this.$t('concluded')
        }
      ]
    }
  },
  methods: {
    ...mapActions({ getKyc: 'GET_KYC' }),
    redirectToCompany () {
      window.location.replace(EXTERNAL_ENDPOINTS.COMPANY.url(this.merchantId))
    },
    updateKycStatus () {
      this.getKyc({ merchant_id: this.merchantId, account_id: this.accountId })
    },
    onError () {
      this.showAlert = true
    }
  }
}
</script>
<style scoped>
@import '@stone-payments/jade-design-tokens/pagarme/space.css';
.wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.container {
  max-width: 90%;
  align-self: center;
  margin-top: var(--global-space-500);
}
.container--kyc-steps {
  max-width: 100%;
}
.stepper {
  margin-bottom: var(--global-space-200);
  width: 100%;
  flex-direction: row;
}
@media (max-width: 767px) {
  .stepper ::v-deep .jade-icon:not(.jade-stepper-item--arrow) {
    display: none;
  }
  .stepper ::v-deep .jade-stepper-item--arrow {
    margin-left: var(--global-space-xsmall);
    margin-right: var(--global-space-xsmall);
  }
}

.container--kyc-content {
  display: flex;
  justify-content: center;
}

.alert {
  margin-bottom: var(--global-space-200);
}

@media (min-width: 768px) {
  .container {
    width: 626px;
  }
}
</style>
<i18n>
  {
    "pt-BR": {
      "navigate": "Ativar conta",
      "validation": "Validação",
      "personalization": "Personalização",
      "concluded": "Concluído",
      "alert-qrcode": "Não foi possível criar o QR Code. Tente outra vez."
    },
    "en-US": {
      "navigate": "Activate account",
      "validation": "Validation",
      "personalization": "Personalization",
      "concluded": "Concluded",
      "alert-qrcode": "Could not create qr code. Try again."
    }
  }
</i18n>
